import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Zap } from "lucide-react";
import { useLang } from "./LanguageContext";
import { TRX_URL } from "./content";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center pt-40 md:pt-36 pb-16 overflow-hidden">
      {/* background glows */}
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -top-40 -right-20 h-[520px] w-[520px] rounded-full bg-[#ccff00]/20 blur-[140px]"
      />
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute bottom-0 -left-40 h-[480px] w-[480px] rounded-full bg-[#00f0ff]/10 blur-[150px]"
      />
      {/* grid lines */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.15]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage: "radial-gradient(circle at 50% 40%, black, transparent 75%)",
          WebkitMaskImage: "radial-gradient(circle at 50% 40%, black, transparent 75%)",
        }}
      />

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="flex h-2 w-2 relative">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#ccff00] opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#ccff00]" />
          </span>
          <span className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-400">
            {t.hero.badge} · {t.hero.overline}
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-[15vw] md:text-[10rem] lg:text-[11rem]">
          <MaskedLine delay={0.35}>
            <span className="text-white">{t.hero.lines[0]}</span>
          </MaskedLine>
          <MaskedLine delay={0.48}>
            <span className="text-stroke-lime">{t.hero.lines[1]}</span>
          </MaskedLine>
          <MaskedLine delay={0.61}>
            <span className="text-[#ccff00]">{t.hero.lines[2]}</span>
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.9 }}
          className="mt-10 grid md:grid-cols-[1fr_auto] gap-8 md:gap-16 items-end"
        >
          <p className="max-w-xl text-base md:text-lg text-zinc-400 font-light leading-relaxed">
            {t.hero.desc}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={TRX_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-register-btn"
              className="group inline-flex items-center justify-center gap-2 h-14 px-8 bg-[#ccff00] text-black font-bold text-sm tracking-wide hover:bg-[#b3e600] transition-colors"
            >
              <Zap className="h-4 w-4" />
              {t.cta.register}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={TRX_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-login-btn"
              className="inline-flex items-center justify-center h-14 px-8 border border-white/20 text-white font-medium text-sm hover:bg-white/5 hover:border-white/40 transition-colors"
            >
              {t.cta.login}
            </a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
